/**
 * Parsing Queue Overview
 * Full-page table view of the parsing review queue.
 * Clicking a row navigates to the split-pane review workstation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useReviewQueueEvents } from '../../contexts/SSEContext';

/**
 * Format a date string to a readable relative time
 */
const formatAge = (dateStr) => {
  if (!dateStr) return '';
  const created = new Date(dateStr);
  const now = new Date();
  const diffMs = now - created;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  return `${diffMins}m ago`;
};

/**
 * Format document type for display
 */
const formatDocType = (type) => {
  const labels = {
    flight: 'Flight',
    hotel: 'Hotel',
    car_rental: 'Car Rental',
    private_flight: 'Private Flight',
    chauffeur_service: 'Chauffeur',
    restaurant: 'Restaurant'
  };
  return labels[type] || type;
};

/**
 * Priority badge component
 */
const PriorityBadge = ({ priority }) => {
  const colors = {
    urgent: 'bg-red-100 text-red-800 border-red-200',
    high: 'bg-orange-100 text-orange-800 border-orange-200',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    low: 'bg-gray-100 text-gray-600 border-gray-200'
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded border ${colors[priority] || colors.medium}`}>
      {priority?.toUpperCase()}
    </span>
  );
};

/**
 * Status badge component
 */
const StatusBadge = ({ status }) => {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800'
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
      {status?.replace('_', ' ')}
    </span>
  );
};

/**
 * Issue count badge
 */
const IssuesBadge = ({ validationErrors = [], missingFields = [] }) => {
  const total = (validationErrors?.length || 0) + (missingFields?.length || 0);
  if (total === 0) return <span className="text-xs text-green-600">None</span>;
  return (
    <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-50 text-red-700 border border-red-200">
      {total} issue{total !== 1 ? 's' : ''}
    </span>
  );
};

/**
 * Extract a summary line from the parsed data
 */
const getSummary = (item) => {
  const data = item.ai_parsed_data || {};
  switch (item.document_type) {
    case 'flight': {
      const legs = data.legs || [];
      if (legs.length > 0) {
        const first = legs[0];
        const route = [first.departure_airport, first.arrival_airport].filter(Boolean).join(' → ');
        return [data.passenger_name, route, data.airline, first.departure_date].filter(Boolean).join(' · ');
      }
      return [data.passenger_name, data.airline, data.confirmation_number].filter(Boolean).join(' · ');
    }
    case 'hotel':
      return [data.guest_name, data.hotel_name, data.check_in_date].filter(Boolean).join(' · ');
    case 'car_rental':
      return [data.driver_name, data.rental_company, data.pickup_date].filter(Boolean).join(' · ');
    case 'private_flight':
      return [data.aircraft_type, data.tail_number, data.operator_name].filter(Boolean).join(' · ');
    case 'chauffeur_service':
      return [data.passenger_name, data.service_company, data.pickup_date].filter(Boolean).join(' · ');
    case 'restaurant':
      return [data.guest_name, data.restaurant_name, data.reservation_date].filter(Boolean).join(' · ');
    default:
      return data.confirmation_number || '';
  }
};

const ParsingQueueOverview = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // State
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    documentType: ''
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);

  // Sort
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Selection & delete
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteConfirmId, setDeleteConfirmId] = useState(null); // single-item confirm
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Load queue items
   */
  const loadItems = useCallback(async () => {
    console.log('📡 loadItems called');
    setLoading(true);
    setError(null);

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const params = new URLSearchParams({
        limit: pageSize,
        offset: page * pageSize,
        sort_by: sortBy,
        sort_order: sortOrder
      });

      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.documentType) params.append('document_type', filters.documentType);

      const response = await axios.get(
        `${apiBase}/admin/parsing-qa/queue?${params.toString()}`,
        { headers }
      );

      setItems(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error loading queue:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize, sortBy, sortOrder]);

  /**
   * Load stats
   */
  const loadStats = useCallback(async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/admin/parsing-qa/queue/stats`, { headers });
      setStats(response.data.stats);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, []);

  useEffect(() => {
    loadItems();
    loadStats();
  }, [loadItems, loadStats]);

  // SSE: real-time updates for queue changes
  const handleSSEEvent = useCallback((event) => {
    console.log('📡 SSE Queue Overview Event:', event.type, event);
    switch (event.type) {
      case 'created':
        // New item - reload to include it
        console.log('📡 SSE: New item created, reloading queue...');
        loadItems();
        loadStats();
        break;
      case 'assigned': {
        const itemData = event.data?.item;
        if (itemData?.id) {
          setItems(prev => prev.map(item =>
            item.id === itemData.id ? { ...item, ...itemData } : item
          ));
        }
        break;
      }
      case 'completed':
      case 'deleted':
        loadItems();
        loadStats();
        break;
      case 'bulk_deleted':
        loadItems();
        loadStats();
        break;
      default:
        break;
    }
  }, [loadItems, loadStats]);

  useReviewQueueEvents(handleSSEEvent, [handleSSEEvent]);

  /**
   * Handle row click - navigate to workstation
   */
  const handleRowClick = useCallback((itemId) => {
    navigate(`/parsing-review/${itemId}`);
  }, [navigate]);

  /**
   * Handle sort toggle
   */
  const handleSort = useCallback((column) => {
    if (sortBy === column) {
      setSortOrder(o => o === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setPage(0);
  }, [sortBy]);

  /**
   * Handle filter change
   */
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  }, []);

  // Clear selection when items change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [items]);

  /**
   * Toggle selection of a single item
   */
  const toggleSelect = useCallback((id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  /**
   * Toggle select all on current page
   */
  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(i => i.id)));
    }
  }, [items, selectedIds.size]);

  /**
   * Delete a single item (with confirmation)
   */
  const handleDeleteSingle = useCallback(async (itemId, e) => {
    e.stopPropagation();
    if (deleteConfirmId !== itemId) {
      setDeleteConfirmId(itemId);
      return;
    }

    setIsDeleting(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.delete(`${apiBase}/admin/parsing-qa/queue/${itemId}`, { headers });
      setDeleteConfirmId(null);
      loadItems();
      loadStats();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove item');
    } finally {
      setIsDeleting(false);
    }
  }, [deleteConfirmId, loadItems, loadStats]);

  /**
   * Bulk delete selected items
   */
  const handleBulkDelete = useCallback(async () => {
    if (!bulkDeleteConfirm) {
      setBulkDeleteConfirm(true);
      return;
    }

    setIsDeleting(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.post(`${apiBase}/admin/parsing-qa/queue/bulk-delete`, {
        item_ids: Array.from(selectedIds)
      }, { headers });
      setBulkDeleteConfirm(false);
      setSelectedIds(new Set());
      loadItems();
      loadStats();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove items');
    } finally {
      setIsDeleting(false);
    }
  }, [bulkDeleteConfirm, selectedIds, loadItems, loadStats]);

  const totalPages = Math.ceil(total / pageSize);
  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < items.length;

  /**
   * Sort indicator
   */
  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-blue-600 ml-1">{sortOrder === 'desc' ? '↓' : '↑'}</span>;
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <header className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Parsing Review Queue</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {total} item{total !== 1 ? 's' : ''} total
              {stats && ` · ${stats.by_status?.pending || 0} pending`}
              {stats && (stats.by_priority?.urgent || 0) > 0 && (
                <span className="text-red-600"> · {stats.by_priority.urgent} urgent</span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Stats badges */}
            {stats && (
              <div className="hidden md:flex items-center gap-2">
                {Object.entries(stats.by_status || {}).filter(([, count]) => count > 0).map(([status, count]) => (
                  <button
                    key={status}
                    onClick={() => handleFilterChange('status', filters.status === status ? '' : status)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      filters.status === status
                        ? 'ring-2 ring-blue-400 ring-offset-1'
                        : ''
                    }`}
                  >
                    <StatusBadge status={status} />
                    <span className="ml-1 text-gray-600">{count}</span>
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={() => { loadItems(); loadStats(); }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {/* Filters bar */}
        <div className="flex items-center gap-3 mt-3">
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          <select
            value={filters.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md"
          >
            <option value="">All Priority</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          <select
            value={filters.documentType}
            onChange={(e) => handleFilterChange('documentType', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md"
          >
            <option value="">All Types</option>
            <option value="flight">Flight</option>
            <option value="hotel">Hotel</option>
            <option value="car_rental">Car Rental</option>
            <option value="private_flight">Private Flight</option>
            <option value="chauffeur_service">Chauffeur</option>
            <option value="restaurant">Restaurant</option>
          </select>

          {(filters.status || filters.priority || filters.documentType) && (
            <button
              onClick={() => { setFilters({ status: '', priority: '', documentType: '' }); setPage(0); }}
              className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 mt-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
            <span className="text-sm text-blue-800 font-medium">
              {selectedIds.size} selected
            </span>
            <button
              onClick={() => { setSelectedIds(new Set()); setBulkDeleteConfirm(false); }}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Clear
            </button>
            <div className="flex-1" />
            {bulkDeleteConfirm ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-red-700">Remove {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''} from queue?</span>
                <button
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                >
                  {isDeleting ? 'Removing...' : 'Confirm'}
                </button>
                <button
                  onClick={() => setBulkDeleteConfirm(false)}
                  disabled={isDeleting}
                  className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1.5 text-sm text-red-600 hover:text-red-800 border border-red-200 rounded hover:bg-red-50 flex items-center gap-1.5"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Remove from queue
              </button>
            )}
          </div>
        )}
      </header>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {error && (
          <div className="mx-6 mt-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md flex items-center justify-between">
            {error}
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 ml-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <table className="w-full">
          <thead className="sticky top-0 bg-gray-50 border-b border-gray-200 z-10">
            <tr>
              <th className="w-10 px-3 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
              </th>
              <th
                className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                onClick={() => handleSort('priority')}
              >
                Priority <SortIcon column="priority" />
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Account / Booker
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subject
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Summary
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Issues
              </th>
              <th
                className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                onClick={() => handleSort('created_at')}
              >
                Age <SortIcon column="created_at" />
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Assigned
              </th>
              <th className="w-20 px-3 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-6 py-12 text-center">
                  <svg className="w-6 h-6 mx-auto animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">Loading queue...</p>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-6 py-12 text-center">
                  <svg className="w-12 h-12 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="mt-2 text-sm font-medium text-gray-900">Queue is empty</p>
                  <p className="text-xs text-gray-500 mt-1">No items match your current filters</p>
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => handleRowClick(item.id)}
                  className={`hover:bg-blue-50 cursor-pointer transition-colors ${
                    selectedIds.has(item.id) ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <td className="w-10 px-3 py-3" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.id)}
                      onChange={(e) => toggleSelect(item.id, e)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-6 py-3">
                    <PriorityBadge priority={item.priority} />
                  </td>
                  <td className="px-6 py-3">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-700">
                    {formatDocType(item.document_type)}
                  </td>
                  <td className="px-6 py-3">
                    <div className="text-sm font-medium text-gray-900 truncate max-w-[180px]">
                      {item.organization_name || '—'}
                    </div>
                    <div className="text-xs text-gray-500 truncate max-w-[180px]">
                      {item.email_from || ''}
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="text-sm text-gray-700 truncate max-w-[250px]" title={item.email_subject}>
                      {item.email_subject || '—'}
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="text-xs text-gray-500 truncate max-w-[250px]" title={getSummary(item)}>
                      {getSummary(item) || '—'}
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <IssuesBadge
                      validationErrors={item.validation_errors}
                      missingFields={item.missing_fields}
                    />
                  </td>
                  <td className="px-6 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {formatAge(item.created_at)}
                  </td>
                  <td className="px-6 py-3 text-xs text-gray-500">
                    {item.assigned_to_user_id ? (
                      <span className={`font-medium ${item.assigned_to_user_id === user?.id ? 'text-green-600' : 'text-blue-600'}`}
                        title={item.assigned_to_user_email || ''}>
                        {item.assigned_to_user_id === user?.id
                          ? 'You'
                          : (item.assigned_to_user_email?.split('@')[0] || 'Assigned')}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="w-20 px-3 py-3" onClick={e => e.stopPropagation()}>
                    {deleteConfirmId === item.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => handleDeleteSingle(item.id, e)}
                          disabled={isDeleting}
                          className="px-1.5 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                        >
                          {isDeleting ? '...' : 'Yes'}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(null); }}
                          className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => handleDeleteSingle(item.id, e)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors"
                        title="Remove from queue"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {/* Page numbers */}
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNum;
                if (totalPages <= 7) {
                  pageNum = i;
                } else if (page < 3) {
                  pageNum = i;
                } else if (page > totalPages - 4) {
                  pageNum = totalPages - 7 + i;
                } else {
                  pageNum = page - 3 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`px-3 py-1.5 text-sm rounded-md ${
                      page === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum + 1}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParsingQueueOverview;
